import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { EProfileTypeDTO } from '@project/view-models';
import { monitorLogger } from '../../../project/monitor/monitor-logger';
import { MediaDevicePreviewSource, PreviewDeviceType, PreviewError, PreviewErrorReason } from '../definitions';
import { MediaDevicePreferencesService } from '../services/media-device-preferences.service';

type PreviewErrorTranslationKey =
  | 'video-calls.preview.errors.device-in-use.camera'
  | 'video-calls.preview.errors.device-in-use.microphone'
  | 'video-calls.preview.errors.permission-denied.camera'
  | 'video-calls.preview.errors.permission-denied.microphone'
  | 'video-calls.preview.errors.device-not-found.camera'
  | 'video-calls.preview.errors.device-not-found.microphone'
  | 'video-calls.preview.errors.unknown.camera'
  | 'video-calls.preview.errors.unknown.microphone';

type PreviewDeviceTranslationKey =
  | 'video-calls.preview.select-camera'
  | 'video-calls.preview.select-microphone'
  | 'video-calls.preview.select-speaker';

/**
 * Camera/microphone preview with device selectors, shared by the pre-join screen, the device
 * settings modal (Settings page) and the in-call device settings panel.
 */
@Component({
  selector: 'app-media-device-preview',
  templateUrl: './media-device-preview.component.html',
  styleUrls: ['./media-device-preview.component.scss'],
})
export class MediaDevicePreviewComponent implements AfterViewInit, OnDestroy {
  @Input() source: MediaDevicePreviewSource;
  @Input() profileType: EProfileTypeDTO;
  @Input() photoUrl: string;
  /** False when the tracks already exist (e.g. published in an ongoing call) and must not be recreated. */
  @Input() acquireTracks = true;
  @Input() trackingScreen = 'videoconference';
  @Input() showPreviewBeforeCallOption = false;

  @ViewChild('videoContainer') private videoContainer: ElementRef<HTMLElement>;

  public cameras: MediaDeviceInfo[] = [];
  public microphones: MediaDeviceInfo[] = [];
  public speakers: MediaDeviceInfo[] = [];

  public selectedCameraId: string | null = null;
  public selectedMicrophoneId: string | null = null;
  public selectedSpeakerId: string | null = null;

  public isPreviewBeforeCallEnabled = !this.mediaDevicePreferencesService.shouldSkipPreviewBeforeCall();

  private devicesChangedSubscription: Subscription;

  constructor(
    private mediaDevicePreferencesService: MediaDevicePreferencesService,
    private notificationService: NotificationsService,
  ) {}

  async ngAfterViewInit(): Promise<void> {
    this.source.setLocalVideoContainer(this.videoContainer.nativeElement);
    this.devicesChangedSubscription = this.source.devicesChangedObservable().subscribe(() => this.loadDevices());

    if (this.acquireTracks) {
      await this.startPreview();
    } else {
      await this.loadDevices();
    }
  }

  ngOnDestroy(): void {
    this.devicesChangedSubscription?.unsubscribe();
    this.source.setLocalVideoContainer(null);
  }

  public async startPreview(): Promise<void> {
    try {
      await this.source.createLocalPreviewTracks();
    } catch (error) {
      console.error('failed to create local preview tracks', error);
    }

    await this.loadDevices();
  }

  private async loadDevices(): Promise<void> {
    try {
      const [cameras, microphones, speakers] = await Promise.all([
        this.source.listCameras(),
        this.source.listMicrophones(),
        this.source.listSpeakers(),
      ]);
      const activeDevices = this.source.getActiveDevices();

      this.cameras = cameras;
      this.microphones = microphones;
      this.speakers = speakers;
      this.selectedCameraId = this.pickSelectedDeviceId(cameras, activeDevices.cameraId);
      this.selectedMicrophoneId = this.pickSelectedDeviceId(microphones, activeDevices.microphoneId);
      this.selectedSpeakerId = this.pickSelectedDeviceId(speakers, activeDevices.speakerId);
    } catch (error) {
      console.error('failed to list preview devices', error);
    }
  }

  private pickSelectedDeviceId(devices: MediaDeviceInfo[], activeDeviceId: string | null): string | null {
    const activeDevice = devices.find((device) => device.deviceId === activeDeviceId);

    return (activeDevice ?? devices[0])?.deviceId ?? null;
  }

  public getDeviceLabel(
    device: MediaDeviceInfo,
    index: number,
    fallbackTranslationKey: PreviewDeviceTranslationKey,
  ): string {
    return device.label || `${TranslateService.localize(fallbackTranslationKey)} ${index + 1}`;
  }

  public getSelectedDeviceLabel(
    devices: MediaDeviceInfo[],
    selectedDeviceId: string | null,
    fallbackTranslationKey: PreviewDeviceTranslationKey,
  ): string {
    const index = devices.findIndex((device) => device.deviceId === selectedDeviceId);

    if (index === -1) {
      return TranslateService.localize(fallbackTranslationKey);
    }

    return this.getDeviceLabel(devices[index], index, fallbackTranslationKey);
  }

  public async onCameraSelect(device: MediaDeviceInfo): Promise<void> {
    const previousCameraId = this.selectedCameraId;
    this.selectedCameraId = device.deviceId;

    try {
      await this.source.switchCamera(device.deviceId);
      this.mediaDevicePreferencesService.savePreferredDevices({ cameraId: device.deviceId });
    } catch (error) {
      console.error('failed to switch camera', error);
      this.selectedCameraId = previousCameraId;
      this.notificationService.error({ message: TranslateService.localize('video-calls.devices.switch-failed') });
    }
  }

  public async onMicrophoneSelect(device: MediaDeviceInfo): Promise<void> {
    const previousMicrophoneId = this.selectedMicrophoneId;
    this.selectedMicrophoneId = device.deviceId;

    try {
      await this.source.switchMicrophone(device.deviceId);
      this.mediaDevicePreferencesService.savePreferredDevices({ microphoneId: device.deviceId });
    } catch (error) {
      console.error('failed to switch microphone', error);
      this.selectedMicrophoneId = previousMicrophoneId;
      this.notificationService.error({ message: TranslateService.localize('video-calls.devices.switch-failed') });
    }
  }

  public onSpeakerSelect(device: MediaDeviceInfo): void {
    this.selectedSpeakerId = device.deviceId;
    this.source.switchSpeaker(device.deviceId);
    this.mediaDevicePreferencesService.savePreferredDevices({ speakerId: device.deviceId });
  }

  public onPreviewBeforeCallChange(isEnabled: boolean): void {
    this.isPreviewBeforeCallEnabled = isEnabled;
    this.mediaDevicePreferencesService.setSkipPreviewBeforeCall(!isEnabled);
  }

  public async toggleVideo(): Promise<void> {
    const isVideoEnabled = await this.source.localVideoObservable().pipe(take(1)).toPromise();

    monitorLogger.screen.trackScreen(
      this.trackingScreen,
      isVideoEnabled ? 'clicar_botao_desligar_camera' : 'clicar_botao_ligar_camera',
    );
    await this.source.toggleVideo();
  }

  public async toggleAudio(): Promise<void> {
    const isAudioEnabled = await this.source.localAudioObservable().pipe(take(1)).toPromise();

    monitorLogger.screen.trackScreen(
      this.trackingScreen,
      isAudioEnabled ? 'clique_botao_desligar_audio' : 'clique_botao_ligar_audio',
    );
    await this.source.toggleSound();
  }

  public getPreviewErrorTranslationKey(previewError: PreviewError): PreviewErrorTranslationKey {
    switch (previewError.reason) {
      case PreviewErrorReason.DeviceInUse:
        return previewError.device === PreviewDeviceType.Camera
          ? 'video-calls.preview.errors.device-in-use.camera'
          : 'video-calls.preview.errors.device-in-use.microphone';
      case PreviewErrorReason.PermissionDenied:
        return previewError.device === PreviewDeviceType.Camera
          ? 'video-calls.preview.errors.permission-denied.camera'
          : 'video-calls.preview.errors.permission-denied.microphone';
      case PreviewErrorReason.DeviceNotFound:
        return previewError.device === PreviewDeviceType.Camera
          ? 'video-calls.preview.errors.device-not-found.camera'
          : 'video-calls.preview.errors.device-not-found.microphone';
      default:
        return previewError.device === PreviewDeviceType.Camera
          ? 'video-calls.preview.errors.unknown.camera'
          : 'video-calls.preview.errors.unknown.microphone';
    }
  }
}
